import { RouterProvider, createBrowserRouter, Navigate } from "react-router-dom";
import { registerRoutePreloads } from "../utils/routePreload";
import { Suspense, lazy } from "react";

import { useAuth } from "../provider/AuthProvider";
import { ProtectedRoute } from "./ProtectedRoute";
import { useInstalledApps } from "../provider/InstalledAppsProvider";
import { SuperAdminRoute } from "./SuperAdminRoute";
import RegistryInitializer from "../components/RegistryInitializer";
import InboxWaitingNotifier from "../components/InboxWaitingNotifier";
import VoiceDeskController from "../components/VoiceDeskController";
import Notification from "../pages/components/Notification";
import RouteErrorBoundary from "../components/RouteErrorBoundary";
import { isChunkLoadError, attemptChunkReload } from "../utils/chunkReload";

// The AI chat overlay is mounted on every authenticated screen but only
// opened on demand, and it pulls in recharts (~800 KB raw) for its
// visualisations. Loading it lazily keeps that out of the main bundle.
const AIChatOverlay = lazy(() => import("../components/AIChat/AIChatOverlay"));

// Wraps React.lazy() so a failed chunk fetch (stale build hash after a
// deploy, or a fetch aborted by navigation on a brand-new tenant's first-ever
// page load) gets one automatic reload instead of crashing straight to the
// error boundary. attemptChunkReload shares its cooldown with the window-level
// listeners in src/index.js, so the two paths cannot both fire a reload; a
// repeat failure inside that window is a real outage and throws through to
// RouteErrorBoundary rather than reload-looping.
function lazyWithRetry(factory) {
  let pending = null;
  const load = () => {
    if (!pending) {
      pending = factory().catch((err) => {
        pending = null; // let a later navigation try again
        throw err;
      });
    }
    return pending;
  };
  const Component = lazy(() =>
    load().catch((err) => {
      if (!isChunkLoadError(err)) throw err;
      if (!attemptChunkReload()) throw err;
      return new Promise(() => {}); // keep Suspense pending until the reload takes effect
    })
  );
  // Warm the chunk ahead of navigation (sidebar hover, idle time). Errors are
  // swallowed here; the real navigation will surface them through Suspense.
  Component.preload = () => load().catch(() => {});
  return Component;
}

// Lazy Load Pages
const MonitoringLayout = lazyWithRetry(() => import("../pages/admin_monitoring/MonitoringLayout"));
const MonitoringDashboard = lazyWithRetry(() => import("../pages/admin_monitoring/MonitoringDashboard"));
const LivePresence = lazyWithRetry(() => import("../pages/admin_monitoring/LivePresence"));
const ActivityLogs = lazyWithRetry(() => import("../pages/admin_monitoring/ActivityLogs"));
const ActiveSessions = lazyWithRetry(() => import("../pages/admin_monitoring/ActiveSessions"));
const LastSeenReport = lazyWithRetry(() => import("../pages/admin_monitoring/LastSeenReport"));
const Homepage = lazyWithRetry(() => import("../pages/components/Homepage"));
const Login = lazyWithRetry(() => import("../pages/Login"));
const MFAVerify = lazyWithRetry(() => import("../pages/MFAVerify"));
const Register = lazyWithRetry(() => import("../pages/Register"));
const VerifyEmail = lazyWithRetry(() => import("../pages/VerifyEmail"));
const Logout = lazyWithRetry(() => import("../pages/Logout"));
const Profile = lazyWithRetry(() => import("../pages/Profile"));
const Dashboard = lazyWithRetry(() => import("../pages/dashboard/Dashboard"));
const AIHome = lazyWithRetry(() => import("../pages/home/AIHome"));
const SaaSAdmin = lazyWithRetry(() => import("../pages/SaaSAdmin"));
const BackupRestore = lazyWithRetry(() => import("../pages/BackupRestore"));
const VoicePlatformPricing = lazyWithRetry(() => import("../pages/VoicePlatformPricing"));

// Helpdesk
const TicketsCreate = lazyWithRetry(() => import("../pages/helpdesk/TicketCreate"));
const TicketsGrid = lazyWithRetry(() => import("../pages/helpdesk/TicketsGrid"));
const TicketsList = lazyWithRetry(() => import("../pages/helpdesk/TicketsList"));
const TicketsForm = lazyWithRetry(() => import("../pages/helpdesk/TicketsViewEdit"));
const TicketDetails = lazyWithRetry(() => import("../pages/helpdesk/TicketDetailsLayout/TicketDetails"));
const HelpdeskDashboard = lazyWithRetry(() => import("../pages/helpdesk/HelpdeskDashboard"));
const HelpdeskReports = lazyWithRetry(() => import("../pages/helpdesk/HelpdeskReports"));
const HelpdeskLayout = lazyWithRetry(() => import("../pages/helpdesk/HelpdeskLayout"));

// Compliance (regulatory) module
const RegulatoryDashboard = lazyWithRetry(() => import("../pages/regulatory/RegulatoryDashboard"));
const MatrixList = lazyWithRetry(() => import("../pages/regulatory/MatrixList"));
const MatrixWorkspace = lazyWithRetry(() => import("../pages/regulatory/MatrixWorkspace"));
const TemplateList = lazyWithRetry(() => import("../pages/regulatory/TemplateList"));
const TemplateEditor = lazyWithRetry(() => import("../pages/regulatory/TemplateEditor"));
const EvidenceLibrary = lazyWithRetry(() => import("../pages/regulatory/EvidenceLibrary"));
const ReviewQueue = lazyWithRetry(() => import("../pages/regulatory/ReviewQueue"));
const RegulatorySettings = lazyWithRetry(() => import("../pages/regulatory/RegulatorySettings"));

// Voice module (LiveKit voice agents) — shells in step 1
const VoiceDashboard = lazyWithRetry(() => import("../pages/voice/VoiceDashboard"));
const AgentDesigner = lazyWithRetry(() => import("../pages/voice/AgentDesigner"));
const CallList = lazyWithRetry(() => import("../pages/voice/CallList"));
const CallDetail = lazyWithRetry(() => import("../pages/voice/CallDetail"));
const TrunkSettings = lazyWithRetry(() => import("../pages/voice/TrunkSettings"));
const VoiceQuality = lazyWithRetry(() => import("../pages/voice/Quality"));
const VoiceQualityCall = lazyWithRetry(() => import("../pages/voice/QualityCallDetail"));
const VoiceCampaigns = lazyWithRetry(() => import("../pages/voice/Campaigns"));
const VoiceLive = lazyWithRetry(() => import("../pages/voice/Live"));

// Contacts & Users
const ContactList = lazyWithRetry(() => import("../pages/contacts/ContactList"));
const ContactsGrid = lazyWithRetry(() => import("../pages/contacts/ContactGrid"));
const ContactCreate = lazyWithRetry(() => import("../pages/contacts/ContactCreate"));
const ContactEdit = lazyWithRetry(() => import("../pages/contacts/ContactEdit"));
const ContactDetails = lazyWithRetry(() => import("../pages/contacts/ContactDetailsLayout/ContactDetails"));
const ContactListMain = lazyWithRetry(() => import("../pages/contacts/ContactListMain"));
const ContactAccountList = lazyWithRetry(() => import("../pages/contacts/ContactAccountList"));
const UsersList = lazyWithRetry(() => import("../pages/users/UsersList"));
const UsersGrid = lazyWithRetry(() => import("../pages/users/UsersGrid"));
const UserCreate = lazyWithRetry(() => import("../pages/users/UserCreate"));
const UserDetails = lazyWithRetry(() => import("../pages/users/UserDetailsLayout/UserDetails"));
const UserEdit = lazyWithRetry(() => import("../pages/users/UserEdit"));
const SLACreate = lazyWithRetry(() => import("../pages/helpdesk/SLACreate"));
// Settings
const Settings = lazyWithRetry(() => import("../pages/settings/Settings"));
const SettingsEdit = lazyWithRetry(() => import("../pages/settings/SettingsEdit"));
const AISettings = lazyWithRetry(() => import("../pages/settings/AISettings"));
const AIPersonas = lazyWithRetry(() => import("../pages/settings/AIPersonas"));
const MfaSettings = lazyWithRetry(() => import("../pages/settings/MfaSettings"));
const DocumentBranding = lazyWithRetry(() => import("../pages/settings/DocumentBranding"));
const ApiKeys = lazyWithRetry(() => import("../pages/settings/ApiKeys"));
const GatewaySettings = lazyWithRetry(() => import("../pages/settings/GatewaySettings"));
const ZohoSettings = lazyWithRetry(() => import("../pages/settings/ZohoSettings"));
const ResourceCalendarList = lazyWithRetry(() => import("../pages/settings/ResourceCalendarList"));
const ResourceCalendarDetails = lazyWithRetry(() => import("../pages/settings/ResourceCalendarDetails"));
const TranslationsSettings = lazyWithRetry(() => import("../pages/settings/TranslationsSettings"));
const EmailConfiguration = lazyWithRetry(() => import("../pages/email/EmailConfiguration"));
const EmailLogs = lazyWithRetry(() => import("../pages/email/EmailLogs"));

// Company
const CompanyCreate = lazyWithRetry(() => import("../pages/company/CompanyCreate"));
const CompanyDetails = lazyWithRetry(() => import("../pages/company/CompanyDetailsLayout/CompanyDetails"));
const CompanyEdit = lazyWithRetry(() => import("../pages/company/CompanyEdit"));
const CompanyCurrentEdit = lazyWithRetry(() => import("../pages/company/CompanyCurrentEdit"));
const CompanyList = lazyWithRetry(() => import("../pages/company/CompanyList"));
const CompanyGrid = lazyWithRetry(() => import("../pages/company/CompanyGrid"));

// Marketing
const Marketing = lazyWithRetry(() => import("../pages/marketing/Marketing"));
const MarketingDashboard = lazyWithRetry(() => import("../pages/marketing/MarketingDashboard"));
const CampaignsList = lazyWithRetry(() => import("../pages/marketing/CampaignsList"));
const CampaignAnalytics = lazyWithRetry(() => import("../pages/marketing/CampaignAnalytics"));
const ContactsAudience = lazyWithRetry(() => import("../pages/marketing/ContactsAudience"));
const FlowBuilder = lazyWithRetry(() => import("../pages/marketing/CampaignBuilder/FlowBuilder"));
const TemplatesList = lazyWithRetry(() => import("../pages/email/TemplatesList"));
const MarketingTemplatesList = lazyWithRetry(() => import("../pages/marketing/MarketingTemplatesList"));
const LoyaltyDashboard = lazyWithRetry(() => import("../pages/marketing/LoyaltyDashboard"));

// Social Media
const SocialMediaLayout = lazyWithRetry(() => import("../pages/social_media/SocialMediaLayout"));
const SocialDashboard = lazyWithRetry(() => import("../pages/social_media/SocialDashboard"));
const SocialPosts = lazyWithRetry(() => import("../pages/social_media/SocialPosts"));
const CampaignCalendar = lazyWithRetry(() => import("../pages/social_media/CampaignCalendar"));
const ListeningMonitors = lazyWithRetry(() => import("../pages/social_media/ListeningMonitors"));
const SocialMediaSettings = lazyWithRetry(() => import("../pages/social_media/SocialMediaSettings"));
const SocialConnections = lazyWithRetry(() => import("../pages/social_media/SocialConnections"));
const CrisisCenter = lazyWithRetry(() => import("../pages/social_media/CrisisCenter"));

// Sentiment Intel
const SentimentDashboard = lazyWithRetry(() => import("../pages/sentiment/SentimentDashboard"));
const SentimentSettings = lazyWithRetry(() => import("../pages/sentiment/SentimentSettings"));
const SentimentJourney = lazyWithRetry(() => import("../pages/sentiment/SentimentJourney"));
const SentimentCoaching = lazyWithRetry(() => import("../pages/sentiment/SentimentCoaching"));
const SentimentCompetitors = lazyWithRetry(() => import("../pages/sentiment/SentimentCompetitors"));
const SentimentBriefingHistory = lazyWithRetry(() => import("../pages/sentiment/SentimentBriefingHistory"));

// Knowledge System
const KnowledgeBaseLayout = lazyWithRetry(() => import("../pages/knowledge_system/layout/KnowledgeBaseLayout"));
const KnowledgeDashboard = lazyWithRetry(() => import("../pages/knowledge_system/Dashboard"));
const KnowledgeHome = lazyWithRetry(() => import("../pages/knowledge_system/KnowledgeHome"));
const ArticleList = lazyWithRetry(() => import("../pages/knowledge_system/ArticleList"));
const ArticleDetail = lazyWithRetry(() => import("../pages/knowledge_system/ArticleDetailsLayout/ArticleDetails"));
const ArticleCreate = lazyWithRetry(() => import("../pages/knowledge_system/ArticleCreate"));
const ArticleEdit = lazyWithRetry(() => import("../pages/knowledge_system/ArticleEdit"));
const KnowledgeCategoryList = lazyWithRetry(() => import("../pages/knowledge_system/KnowledgeCategoryList"));
const KnowledgeTagList = lazyWithRetry(() => import("../pages/knowledge_system/KnowledgeTagList"));

// Others
const AppStore = lazyWithRetry(() => import("../pages/apps/AppStore"));
const Inbox = lazyWithRetry(() => import("../pages/inbox/Inbox"));
const InboxDashboard = lazyWithRetry(() => import("../pages/inbox/components/InboxDashboard"));
const WhatsAppTemplates = lazyWithRetry(() => import("../pages/inbox/components/WhatsAppTemplates"));
const ChatHistory = lazyWithRetry(() => import("../pages/inbox/components/ChatHistory"));
const ChatbotList = lazyWithRetry(() => import("../pages/inbox/components/ChatbotList"));
const ChatbotBuilder = lazyWithRetry(() => import("../pages/inbox/components/ChatbotBuilder"));
const ChannelsConfig = lazyWithRetry(() => import("../pages/inbox/components/ChannelsConfig"));
const PublicSurvey = lazyWithRetry(() => import("../pages/customer_satisfaction/PublicSurvey"));

// CSAT
const CSATDashboard = lazyWithRetry(() => import("../pages/customer_satisfaction/CSATDashboard"));
const SurveyList = lazyWithRetry(() => import("../pages/customer_satisfaction/SurveyList"));
const SurveyCreate = lazyWithRetry(() => import("../pages/customer_satisfaction/SurveyCreate"));
const SurveyResponses = lazyWithRetry(() => import("../pages/customer_satisfaction/SurveyResponses"));
const CSATReports = lazyWithRetry(() => import("../pages/customer_satisfaction/CSATReports"));

// NEXUS — Agentic AI Studio
const NexusDashboard = lazyWithRetry(() => import("../pages/nexus/NexusDashboard"));
const NexusAgentsList = lazyWithRetry(() => import("../pages/nexus/AgentsList"));
const NexusSkillsList = lazyWithRetry(() => import("../pages/nexus/SkillsList"));
const NexusRunDetail = lazyWithRetry(() => import("../pages/nexus/RunDetail"));
const NexusTeamsList = lazyWithRetry(() => import("../pages/nexus/TeamsList"));
const NexusTools = lazyWithRetry(() => import("../pages/nexus/ToolsManager"));
const NexusApprovals = lazyWithRetry(() => import("../pages/nexus/ApprovalInbox"));
const NexusVault = lazyWithRetry(() => import("../pages/nexus/CredentialVault"));
const NexusMCP = lazyWithRetry(() => import("../pages/nexus/MCPConfig"));
const NexusObservability = lazyWithRetry(() => import("../pages/nexus/Observability"));
const NexusBenchmark = lazyWithRetry(() => import("../pages/nexus/Benchmark"));
const NexusSettings = lazyWithRetry(() => import("../pages/nexus/NexusSettings"));

// CRM
const CRM = lazyWithRetry(() => import("../pages/partner/CRM"));
const LeadCreate = lazyWithRetry(() => import("../pages/partner/LeadCreate"));
const LeadDashboard = lazyWithRetry(() => import("../pages/partner/LeadDashboard"));
const CRMDashboard = lazyWithRetry(() => import("../pages/partner/CRMDashboard"));
const LeadList = lazyWithRetry(() => import("../pages/partner/LeadList"));
const OpportunityBoard = lazyWithRetry(() => import("../pages/partner/opportunities/OpportunityBoard"));
const OpportunityCreate = lazyWithRetry(() => import("../pages/partner/opportunities/OpportunityCreate"));
const OpportunityDetail = lazyWithRetry(() => import("../pages/partner/opportunities/OpportunityDetail"));
const AccountDetail = lazyWithRetry(() => import("../pages/partner/AccountDetail"));
const AccountEdit = lazyWithRetry(() => import("../pages/partner/AccountEdit"));
const ProposalPreviewPage = lazyWithRetry(() => import("../pages/partner/opportunities/ProposalPreviewPage"));
const Forecasting = lazyWithRetry(() => import("../pages/partner/Forecasting"));
const CRMTasks = lazyWithRetry(() => import("../pages/partner/tasks/CRMTasks"));
const CRMReports = lazyWithRetry(() => import("../pages/partner/CRMReports"));
const WebToLeadForm = lazyWithRetry(() => import("../pages/partner/WebToLeadForm"));
const AssignmentRules = lazyWithRetry(() => import("../pages/partner/AssignmentRules"));
const EmailSequences = lazyWithRetry(() => import("../pages/partner/EmailSequences"));
const RevenueIntelligence = lazyWithRetry(() => import("../pages/partner/RevenueIntelligence"));
const MRRDashboard = lazyWithRetry(() => import("../pages/partner/MRRDashboard"));
const ContractsList = lazyWithRetry(() => import("../pages/partner/ContractsList"));
const MeetingScheduler = lazyWithRetry(() => import("../pages/partner/MeetingScheduler"));

// E-Sign (public — no auth)
const PublicSignPage = lazyWithRetry(() => import("../pages/esign/PublicSignPage"));

// Meeting Scheduler (public — no auth)
const PublicBookingPage = lazyWithRetry(() => import("../pages/scheduling/PublicBookingPage"));
const PublicCancelPage = lazyWithRetry(() => import("../pages/scheduling/PublicBookingPage").then(m => ({ default: m.PublicCancelPage })));

// Customer Portal (public — portal-token auth, not Django auth)
const CustomerPortal = lazyWithRetry(() => import("../pages/portal/CustomerPortal"));

// Integration Hub
const IntegrationHub = lazyWithRetry(() => import("../pages/integrations/IntegrationHub"));

// Projects
const ProjectsModuleDashboard = lazyWithRetry(() => import("../pages/projects/ProjectsModuleDashboard"));
const AllProjectsOverview = lazyWithRetry(() => import("../pages/projects/AllProjectsOverview"));
const MyTasksView = lazyWithRetry(() => import("../pages/projects/MyTasksView"));
const ProjectsList = lazyWithRetry(() => import("../pages/projects/ProjectsList"));
const ProjectOverview = lazyWithRetry(() => import("../pages/projects/ProjectOverview"));
const ProjectBoard = lazyWithRetry(() => import("../pages/projects/ProjectBoard"));
const ProjectListView = lazyWithRetry(() => import("../pages/projects/ProjectListView"));
const ProjectGantt = lazyWithRetry(() => import("../pages/projects/ProjectGantt"));
const ProjectMilestones = lazyWithRetry(() => import("../pages/projects/ProjectMilestones"));
const ProjectTimesheets = lazyWithRetry(() => import("../pages/projects/ProjectTimesheets"));
const TimesheetApprovalDashboard = lazyWithRetry(() => import("../pages/projects/TimesheetApprovalDashboard"));
const WorkloadView = lazyWithRetry(() => import("../pages/projects/WorkloadView"));
const ProjectSprints = lazyWithRetry(() => import("../pages/projects/ProjectSprints"));
const ProjectTemplates = lazyWithRetry(() => import("../pages/projects/ProjectTemplates"));
const ProjectReports = lazyWithRetry(() => import("../pages/projects/ProjectReports"));

// Huddle — Collaboration
const HuddlePage = lazyWithRetry(() => import("../pages/huddle/Huddle"));

// CPQ
const ProductCreate = lazyWithRetry(() => import("../pages/partner/products/ProductCreate"));
const ProductDetail = lazyWithRetry(() => import("../pages/partner/products/ProductDetail"));

// Helpdesk - SLA
const SLAList = lazyWithRetry(() => import("../pages/helpdesk/SLAList"));
const SLADetails = lazyWithRetry(() => import("../pages/helpdesk/SLADetailsLayout/SLADetails"));
const SLAEdit = lazyWithRetry(() => import("../pages/helpdesk/SLAEdit"));

// Helpdesk - Category
const CategoryList = lazyWithRetry(() => import("../pages/helpdesk/Category/CategoryList"));
const CategoryCreate = lazyWithRetry(() => import("../pages/helpdesk/Category/CategoryCreate"));
const CategoryDetails = lazyWithRetry(() => import("../pages/helpdesk/Category/CategoryDetailsLayout/CategoryDetails"));
const CategoryEdit = lazyWithRetry(() => import("../pages/helpdesk/Category/CategoryEdit"));

// Helpdesk - Team
const TeamList = lazyWithRetry(() => import("../pages/helpdesk/Team/TeamList"));
const TeamCreate = lazyWithRetry(() => import("../pages/helpdesk/Team/TeamCreate"));
const TeamDetails = lazyWithRetry(() => import("../pages/helpdesk/Team/TeamDetailsLayout/TeamDetails"));
const TeamEdit = lazyWithRetry(() => import("../pages/helpdesk/Team/TeamEdit"));

// Search
const GlobalSearchResults = lazyWithRetry(() => import("../pages/search/GlobalSearchResults"));
const Unauthorized = lazyWithRetry(() => import("../pages/Unauthorized"));
const TrialExpired = lazyWithRetry(() => import("../pages/TrialExpired"));

// Calendar
const CalendarApp = lazyWithRetry(() => import("../pages/calendar/CalendarApp"));
const PublicBookingApp = lazyWithRetry(() => import("../pages/calendar/public/PublicBookingApp"));
const CalendarIntegrations = lazyWithRetry(() => import("../pages/calendar/settings/CalendarIntegrations"));
const IntegrationCallback = lazyWithRetry(() => import("../pages/calendar/settings/IntegrationCallback"));
const AppointmentsDashboard = lazyWithRetry(() => import("../pages/appointments/AppointmentsDashboard"));
const AppointmentDetail = lazyWithRetry(() => import("../pages/appointments/AppointmentDetail"));
const ServiceBooking = lazyWithRetry(() => import("../pages/appointments/ServiceBooking"));
const WorkingHours = lazyWithRetry(() => import("../pages/appointments/WorkingHours"));

// "/" opens the AI home where the workspace has the assistant (AI switched on
// and Nexus installed), and the dashboard everywhere else.
const HomeIndex = () => {
  const { isAppInstalled, aiEnabled, isAppsLoading } = useInstalledApps();
  if (isAppsLoading) return null;
  return <Navigate to={aiEnabled && isAppInstalled('nexus') ? "/home" : "/dashboard"} replace />;
};

// ── Route prefetch ────────────────────────────────────────────────────────
// Sidebar entries warm their page chunk on hover/focus so the click lands on
// code that is already here. Longest prefix wins; unknown paths are a no-op.
const COMPLIANCE_GROUPS = [
  'Compliance / Viewer', 'Compliance / Contributor', 'Compliance / Reviewer', 'Compliance / Approver',
  'Compliance / Admin', 'Compliance / Auditor', 'Administration / Settings',
  'Helpdesk / User', 'Helpdesk / Support Agent', 'Helpdesk / Manager',
];

const VOICE_GROUPS = ['Voice / Viewer', 'Voice / Agent', 'Voice / Supervisor', 'Voice / Admin', 'Administration / Settings'];

const PRELOAD_BY_PATH = {
  '/home': [AIHome],
  '/dashboard': [Dashboard],
  '/huddle': [HuddlePage],
  '/inbox': [Inbox],
  '/contacts': [ContactListMain],
  '/tickets': [TicketsGrid],
  '/tickets/list': [TicketsList],
  '/compliance': [RegulatoryDashboard],
  '/compliance/matrices': [MatrixList],
  '/voice': [VoiceDashboard],
  '/voice/calls': [CallList],
  '/knowledge-system': [KnowledgeBaseLayout],
  '/csat': [CSATDashboard],
  '/crm': [CRM],
  '/projects': [ProjectsModuleDashboard],
  '/appointments': [AppointmentsDashboard],
  '/marketing': [Marketing],
  '/social-media': [SocialMediaLayout],
  '/admin/monitoring': [MonitoringLayout],
  '/users': [UsersGrid],
  '/company': [CompanyCurrentEdit],
  '/nexus': [NexusDashboard],
  '/apps': [AppStore],
  '/saas-admin': [SaaSAdmin],
};

registerRoutePreloads(PRELOAD_BY_PATH);

const Routes = () => {
  const { token } = useAuth();

  // Public Routes (Accessible by anyone)
  const publicRoutes = [
    // Email-verification landing — must work whether or not the visitor is logged in.
    {
      path: "/verify-email",
      element: <VerifyEmail />
    },
    {
      path: "/verify-email/:token",
      element: <VerifyEmail />
    },
    {
      path: "/s/:uuid",
      element: <PublicSurvey />
    },
    {
      path: "/proposals/preview/:quoteId",
      element: <ProposalPreviewPage />
    },
    {
      path: "/lead-form",
      element: <WebToLeadForm />
    },
    // ── Public E-Sign ── no auth required
    {
      path: "/sign/:token",
      element: <PublicSignPage />
    },
    // ── Public Meeting Booking ── no auth required
    {
      path: "/schedule/:slug",
      element: <PublicBookingPage />
    },
    {
      path: "/schedule/cancel/:cancel_token",
      element: <PublicCancelPage />
    },
    // ── Customer Self-Service Portal ── uses portal-token auth
    {
      path: "/portal/:slug",
      element: <CustomerPortal />
    },
    {
      path: "/portal/:slug/verify",
      element: <CustomerPortal />
    },
    // ── Calendar Shared-Availability Booking ── no auth required
    {
      path: "/book/:slug",
      element: <PublicBookingApp />
    },
  ];

  const routesForAuthenticatedOnly = [
    {
      path: "/",
      // Notification is mounted once here so showNotification() works on every
      // authenticated screen, not only the handful that used to mount it.
      element: <><RegistryInitializer /><ProtectedRoute /><Suspense fallback={null}><AIChatOverlay /></Suspense><InboxWaitingNotifier /><VoiceDeskController /><Notification /></>,
      children: [
        {
          index: true,
          element: <HomeIndex />,
        },
        {
          element: <ProtectedRoute requiredApp="nexus" requiresAi />,
          children: [
            { path: "/home", element: <AIHome /> },
            { path: "/home/c/:conversationId", element: <AIHome /> },
          ],
        },
        {
          path: "/dashboard",
          element: <Dashboard />,
        },
        {
          path: "/profile",
          element: <Profile />,
        },
        {
          path: "/unauthorized",
          element: <Unauthorized />,
        },
        {
          path: "/trial-expired",
          element: <TrialExpired />,
        },
        // ──────────────────────────────────────────────────────────
        // HELPDESK / TICKETS ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Helpdesk / Viewer', 'Helpdesk / User', 'Helpdesk / Support Agent', 'Helpdesk / Manager']} requiredApp="helpdesk" />,
          children: [
            {
              path: "/tickets",
              element: <TicketsGrid />,
            },
            {
              path: "/tickets/list",
              element: <TicketsList />,
            },
            {
              path: "/tickets/create",
              element: <TicketsCreate />,
            },
            {
              path: "/tickets/:ticketId/edit",
              element: <TicketsForm />,
            },
            {
              path: "/tickets/:ticketId",
              element: <TicketDetails />,
            },
          ]
        },
        // ──────────────────────────────────────────────────────────
        // COMPLIANCE (regulatory) ROUTES — department members reach the
        // workspace through their Helpdesk groups; the API scopes what they see.
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={COMPLIANCE_GROUPS} requiredApp="regulatory" />,
          children: [
            { path: "/compliance", element: <RegulatoryDashboard /> },
            { path: "/compliance/matrices", element: <MatrixList /> },
            { path: "/compliance/matrices/:matrixId", element: <MatrixWorkspace /> },
            { path: "/compliance/templates", element: <TemplateList /> },
            { path: "/compliance/templates/:templateId", element: <TemplateEditor /> },
            { path: "/compliance/evidence", element: <EvidenceLibrary /> },
          ]
        },
        {
          element: <ProtectedRoute allowedGroups={['Compliance / Reviewer', 'Compliance / Approver', 'Compliance / Admin', 'Administration / Settings']} requiredApp="regulatory" />,
          children: [
            { path: "/compliance/review", element: <ReviewQueue /> },
          ]
        },
        {
          element: <ProtectedRoute allowedGroups={['Compliance / Admin', 'Administration / Settings']} requiredApp="regulatory" />,
          children: [
            { path: "/compliance/settings", element: <RegulatorySettings /> },
          ]
        },
        // ──────────────────────────────────────────────────────────
        // VOICE ROUTES — AI voice agents (modules.voice). Telephony
        // settings (trunks, numbers) are Admin only.
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={VOICE_GROUPS} requiredApp="voice" />,
          children: [
            { path: "/voice", element: <VoiceDashboard /> },
            { path: "/voice/agents", element: <AgentDesigner /> },
            { path: "/voice/agents/:agentId", element: <AgentDesigner /> },
            { path: "/voice/calls", element: <CallList /> },
            { path: "/voice/calls/:callId", element: <CallDetail /> },
            { path: "/voice/quality", element: <VoiceQuality /> },
            { path: "/voice/quality/calls/:callId", element: <VoiceQualityCall /> },
            { path: "/voice/campaigns", element: <VoiceCampaigns /> },
            { path: "/voice/live", element: <VoiceLive /> },
          ]
        },
        {
          element: <ProtectedRoute allowedGroups={['Voice / Admin', 'Administration / Settings']} requiredApp="voice" />,
          children: [
            { path: "/voice/settings", element: <TrunkSettings /> },
          ]
        },
        // Helpdesk Dashboard & Reports (Manager only)
        {
          element: <ProtectedRoute allowedGroups={['Helpdesk / Manager', 'Administration / Settings']} requiredApp="helpdesk" />,
          children: [
            {
              path: "/tickets/dashboard",
              element: <HelpdeskDashboard />,
            },
            {
              path: "/tickets/reports",
              element: <HelpdeskReports />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // CONTACTS ROUTES (the partner app; a CRM role opens them too)
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Contacts / Viewer', 'Contacts / User', 'Contacts / Manager', 'CRM / Viewer', 'CRM / User', 'CRM / Manager']} requiredApp="partner" />,
          children: [
            {
              path: "/contacts",
              element: <ContactListMain />,
            },
            {
              path: "/contacts/list",
              element: <ContactListMain />,
            },
            {
              path: "/contacts/accounts",
              element: <ContactAccountList />,
            },
            {
              path: "/contacts/accounts/create",
              element: <AccountEdit />,
            },
            {
              path: "/contacts/accounts/:id",
              element: <AccountDetail />,
            },
            {
              path: "/contacts/accounts/:id/edit",
              element: <AccountEdit />,
            },
            {
              path: "/contacts/create",
              element: <ContactCreate />,
            },
            {
              path: "/contacts/edit/:id",
              element: <ContactEdit />,
            },
            {
              path: "/contacts/:id",
              element: <ContactDetails />,
            },
          ]
        },

        // SaaS Superadmin Control — superuser ONLY
        {
          element: <SuperAdminRoute />,
          children: [
            {
              path: "/saas-admin",
              element: <SaaSAdmin />,
            },
            {
              path: "/saas-admin/backups",
              element: <BackupRestore />,
            },
            {
              path: "/saas-admin/voice-pricing",
              element: <VoicePlatformPricing />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // ADMINISTRATION / USERS & SETTINGS
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Access Rights']} />,
          children: [
            {
              path: "/users/list",
              element: <UsersList />,
            },
            {
              path: "/users",
              element: <UsersGrid />,
            },
            {
              path: "/users/create",
              element: <UserCreate />,
            },
            {
              path: "/users/:id",
              element: <UserDetails />,
            },
            {
              path: "/users/:id/edit",
              element: <UserEdit />,
            },
            {
              path: "settings",
              element: <Settings />,
            },
            {
              path: "settings/edit",
              element: <SettingsEdit />,
            },
            {
              path: "/company/create",
              element: <CompanyCreate />,
            },
            {
              path: "/company",
              element: <CompanyCurrentEdit mode="view" />,
            },
            {
              path: "/company/list",
              element: <CompanyList />,
            },
            {
              path: "/company/:id",
              element: <CompanyDetails />,
            },
            {
              path: "/company/:id/edit",
              element: <CompanyEdit />,
            },
            {
              path: "/company/update-details",
              element: <CompanyCurrentEdit />,
            },
            {
              path: "settings/translations",
              element: <TranslationsSettings />,
            },
            {
              path: "settings/ai",
              element: <AISettings />,
            },
            {
              path: "settings/ai-personas",
              element: <AIPersonas />,
            },
            {
              path: "settings/mfa",
              element: <MfaSettings />,
            },
            {
              path: "settings/document-branding",
              element: <DocumentBranding />,
            },
            {
              path: "settings/api-keys",
              element: <ApiKeys />,
            },
            {
              path: "settings/gateways",
              element: <GatewaySettings />,
            },
            {
              path: "settings/zoho",
              element: <ZohoSettings />,
            },
            {
              path: "settings/resource-calendars",
              element: <ResourceCalendarList />,
            },
            {
              path: "settings/resource-calendars/create",
              element: <ResourceCalendarDetails />,
            },
            {
              path: "settings/resource-calendars/:id",
              element: <ResourceCalendarDetails />,
            },
            {
              path: "/apps",
              element: <AppStore />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // EMAIL SERVICES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Email / Manager', 'Administration / Settings', 'Administration / Access Rights']} />,
          children: [
            {
              path: "settings/emails",
              element: <EmailConfiguration />,
            },
            {
              path: "settings/emails/create",
              element: <EmailConfiguration />,
            },
            {
              path: "settings/emails/:id",
              element: <EmailConfiguration />,
            },
            {
              path: "settings/emails/:id/edit",
              element: <EmailConfiguration />,
            },
            {
              path: "/email-logs",
              element: <EmailLogs />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // HELPDESK CONFIGURATION (SLA, CATEGORY, TEAM)
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Helpdesk / Manager', 'Administration / Settings']} requiredApp="helpdesk" />,
          children: [
            // SLA Routes
            {
              path: "/sla/list",
              element: <SLAList />,
            },
            {
              path: "/sla/create",
              element: <SLACreate />,
            },
            {
              path: "/sla/:id",
              element: <SLADetails />,
            },
            {
              path: "/sla/edit/:id",
              element: <SLAEdit />,
            },
            // Category Routes
            {
              path: "/category/list",
              element: <CategoryList />,
            },
            {
              path: "/category/create",
              element: <CategoryCreate />,
            },
            {
              path: "/category/:id",
              element: <CategoryDetails />,
            },
            {
              path: "/category/edit/:id",
              element: <CategoryEdit />,
            },
            // Team Routes
            {
              path: "/team/list",
              element: <TeamList />,
            },
            {
              path: "/team/create",
              element: <TeamCreate />,
            },
            {
              path: "/team/:id",
              element: <TeamDetails />,
            },
            {
              path: "/team/edit/:id",
              element: <TeamEdit />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // KNOWLEDGE SYSTEM ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Knowledge System / Viewer', 'Knowledge System / Author', 'Knowledge System / Manager']} requiredApp="knowledge_system" />,
          children: [
            {
              path: "/knowledge-system",
              element: <KnowledgeBaseLayout />,
              children: [
                {
                  index: true,
                  element: <KnowledgeHome />,
                },
                {
                  path: "list",
                  element: <ArticleList />,
                },
                {
                  path: "dashboard",
                  element: <KnowledgeDashboard />,
                },
                {
                  path: "category/:categoryId",
                  element: <ArticleList />,
                },
                {
                  path: "article/:articleId",
                  element: <ArticleDetail />,
                },
                {
                  element: <ProtectedRoute allowedGroups={['Knowledge System / Manager', 'Administration / Settings']} />,
                  children: [
                    {
                      path: "create",
                      element: <ArticleCreate />,
                    },
                    {
                      path: "article/:articleId/edit",
                      element: <ArticleEdit />,
                    },
                    {
                      path: "categories",
                      element: <KnowledgeCategoryList />,
                    },
                    {
                      path: "tags",
                      element: <KnowledgeTagList />,
                    },
                  ]
                },
              ]
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // MARKETING ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Marketing / Viewer', 'Marketing / User', 'Marketing / Manager']} requiredApp="marketing" />,
          children: [
            {
              path: "/marketing",
              element: <Marketing />,
            },
            {
              path: "/marketing/campaigns",
              element: <Marketing />,
            },
            {
              path: "/marketing/calendar",
              element: <Marketing />,
            },
            {
              path: "/marketing/contacts",
              element: <Marketing />,
            },
            {
              path: "/marketing/personas",
              element: <Marketing />,
            },
            {
              path: "/marketing/scoring",
              element: <Marketing />,
            },
            {
              path: "/marketing/content",
              element: <Marketing />,
            },
            {
              path: "/marketing/capping",
              element: <Marketing />,
            },
            {
              path: "/marketing/templates",
              element: <Marketing />,
            },
            {
              path: "/email/templates",
              element: <TemplatesList />,
            },
            {
              element: <ProtectedRoute allowedGroups={['Marketing / Manager', 'Administration / Settings']} />,
              children: [
                {
                  path: "/marketing/campaigns/builder",
                  element: <FlowBuilder />,
                },
                {
                  path: "/marketing/campaigns/builder/:id",
                  element: <FlowBuilder />,
                },
              ]
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // LOYALTY ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Marketing / Viewer', 'Marketing / User', 'Marketing / Manager']} requiredApp="loyalty" />,
          children: [
            {
              path: "/loyalty",
              element: <Navigate to="/loyalty/overview" replace />,
            },
            {
              path: "/loyalty/:tab",
              element: <LoyaltyDashboard />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // SOCIAL MEDIA ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Social Media / Viewer', 'Social Media / Agent', 'Social Media / Manager', 'Administration / Settings']} requiredApp="social_media" />,
          children: [
            {
              path: "/social-media",
              element: <SocialMediaLayout />,
              children: [
                { path: "", element: <Navigate to="dashboard" replace /> },
                { path: "dashboard", element: <SocialDashboard /> },
                { path: "posts", element: <SocialPosts /> },
                { path: "calendar", element: <CampaignCalendar /> },
                { path: "listening", element: <ListeningMonitors /> },
                { path: "connections", element: <SocialConnections /> },
                { path: "settings", element: <SocialMediaSettings /> },
                { path: "crisis", element: <CrisisCenter /> },
              ]
            }
          ]
        },

        // ──────────────────────────────────────────────────────────
        // SENTIMENT INTEL ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Sentiment Intel / Viewer', 'Sentiment Intel / Manager', 'Administration / Settings']} requiredApp="sentiment_analysis" requiresAi />,
          children: [
            { path: "/sentiment", element: <Navigate to="/sentiment/dashboard" replace /> },
            { path: "/sentiment/dashboard", element: <SentimentDashboard /> },
            { path: "/sentiment/journey", element: <SentimentJourney /> },
            { path: "/sentiment/coaching", element: <SentimentCoaching /> },
            { path: "/sentiment/competitors", element: <SentimentCompetitors /> },
            { path: "/sentiment/briefing-history", element: <SentimentBriefingHistory /> },
            { path: "/sentiment/settings", element: <SentimentSettings /> },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // INBOX ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Inbox / Viewer', 'Inbox / Agent', 'Inbox / Manager']} requiredApp="inbox" />,
          children: [
            {
              path: "/inbox",
              element: <Inbox />,
              children: [
                {
                  path: "",
                  element: <Navigate to="/inbox/chats" replace />,
                },
                {
                  path: "chats",
                  element: <Inbox />,
                },
                {
                  path: "dashboard",
                  element: <Inbox initialTab="dashboard" />,
                },
                {
                  path: "templates",
                  element: <Inbox initialTab="template" />,
                },
                {
                  path: "history",
                  element: <Inbox initialTab="history" />,
                },
                {
                  path: "chatbots",
                  element: <Inbox initialTab="chatbot" />,
                },
                {
                  path: "chatbots/new",
                  element: <ChatbotBuilder bot={{}} onBack={() => window.history.back()} />,
                },
                {
                  path: "chatbots/:id",
                  element: <ChatbotBuilder bot={{ id: 1 }} onBack={() => window.history.back()} />,
                },
{
          path: "channels",
          element: <Inbox initialTab="channels" />,
        },
      ],
    },
  ]
},

        // ──────────────────────────────────────────────────────────
        // CSAT ROUTES
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['CSAT / Viewer', 'CSAT / User', 'CSAT / Manager']} requiredApp="customer_satisfaction" />,
          children: [
            {
              path: "/csat/surveys",
              element: <SurveyList />,
            },
            {
              path: "/csat/surveys/:id",
              element: <SurveyCreate />,
            },
            {
              path: "/csat/surveys/:id/responses",
              element: <SurveyResponses />,
            },
            {
              element: <ProtectedRoute allowedGroups={['CSAT / Manager', 'Administration / Settings']} />,
              children: [
                {
                  path: "/csat",
                  element: <CSATDashboard />,
                },
                {
                  path: "/csat/surveys/create",
                  element: <SurveyCreate />,
                },
                {
                  path: "/csat/surveys/:id/edit",
                  element: <SurveyCreate />,
                },
                {
                  path: "/csat/reports",
                  element: <CSATReports />,
                },
              ]
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // CRM ROUTES (the crm app, installed on top of Contacts)
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['CRM / Viewer', 'CRM / User', 'CRM / Manager']} requiredApp="crm" />,
          children: [
            {
              path: "/crm",
              element: <CRM />,
            },
            {
              path: "/crm/leads",
              element: <CRM />,
            },
            {
              path: "/crm/opportunities",
              element: <CRM />,
            },
            {
              path: "/crm/accounts",
              element: <CRM />,
            },
            {
              path: "/crm/contacts",
              element: <CRM />,
            },
            {
              path: "/crm/products",
              element: <CRM />,
            },
            {
              path: "/crm/leads/:id/view",
              element: <LeadDashboard />,
            },
            {
              path: "/crm/accounts/:id",
              element: <AccountDetail />,
            },
            {
              element: <ProtectedRoute allowedGroups={['CRM / Manager', 'Administration / Settings']} />,
              children: [
                {
                  path: "/crm/dashboard",
                  element: <CRM />,
                },
                {
                  path: "/crm/leads/create",
                  element: <LeadCreate />,
                },
                {
                  path: "/crm/leads/:id/edit",
                  element: <LeadCreate />,
                },
                {
                  path: "/crm/accounts/create",
                  element: <AccountEdit />,
                },
                {
                  path: "/crm/accounts/:id/edit",
                  element: <AccountEdit />,
                },
                {
                  path: "/crm/opportunities/create",
                  element: <OpportunityCreate />,
                },
                {
                  path: "/crm/opportunities/:id/edit",
                  element: <OpportunityCreate />,
                },
                {
                  path: "/crm/products/create",
                  element: <ProductCreate />,
                },
                {
                  path: "/crm/products/:id/edit",
                  element: <ProductCreate />,
                },
                {
                  path: "/crm/forecasting",
                  element: <Forecasting />,
                },
                {
                  path: "/crm/tasks",
                  element: <CRMTasks />,
                },
                {
                  path: "/crm/reports",
                  element: <CRMReports />,
                },
                {
                  path: "/crm/assignment-rules",
                  element: <AssignmentRules />,
                },
                {
                  path: "/crm/sequences",
                  element: <EmailSequences />,
                },
                {
                  path: "/crm/revenue",
                  element: <RevenueIntelligence />,
                },
                {
                  path: "/crm/mrr",
                  element: <MRRDashboard />,
                },
                {
                  path: "/crm/contracts",
                  element: <ContractsList />,
                },
                {
                  path: "/crm/scheduler",
                  element: <MeetingScheduler />,
                },
              ]
            },
            {
              path: "/crm/products/:id",
              element: <ProductDetail />,
            },
            {
              path: "/crm/opportunities",
              element: <OpportunityBoard />,
            },
            {
              path: "/crm/opportunities/:id/view",
              element: <OpportunityDetail />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // NEXUS — AGENTIC AI STUDIO
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Access Rights', 'AI & Tech / AI Configurator', 'Nexus / Viewer', 'Nexus / User', 'Nexus / Manager']} requiredApp="nexus" requiresAi />,
          children: [
            {
              path: "/nexus",
              element: <NexusDashboard />,
            },
            {
              path: "/nexus/dashboard",
              element: <NexusDashboard />,
            },
            {
              path: "/nexus/agents",
              element: <NexusAgentsList />,
            },
            {
              path: "/nexus/agents/new",
              element: <NexusAgentsList />,
            },
            {
              path: "/nexus/agents/:id",
              element: <NexusAgentsList />,
            },
            {
              path: "/nexus/agents/:id/edit",
              element: <NexusAgentsList />,
            },
            {
              path: "/nexus/skills",
              element: <NexusSkillsList />,
            },
            {
              path: "/nexus/skills/new",
              element: <NexusSkillsList />,
            },
            {
              path: "/nexus/skills/:id/edit",
              element: <NexusSkillsList />,
            },
            {
              path: "/nexus/runs",
              element: <NexusDashboard />,
            },
            {
              path: "/nexus/runs/:id",
              element: <NexusRunDetail />,
            },
            {
              element: <ProtectedRoute allowedGroups={['Nexus / Manager', 'Nexus / User', 'Nexus / Viewer', 'Administration / Settings', 'Administration / Access Rights', 'AI & Tech / AI Configurator']} />,
              children: [
                {
                  path: "/nexus/teams",
                  element: <NexusTeamsList />,
                },
                {
                  path: "/nexus/tools",
                  element: <NexusTools />,
                },
                {
                  path: "/nexus/approvals",
                  element: <NexusApprovals />,
                },
                {
                  path: "/nexus/vault",
                  element: <NexusVault />,
                },
                {
                  path: "/nexus/mcp",
                  element: <NexusMCP />,
                },
                {
                  path: "/nexus/observability",
                  element: <NexusObservability />,
                },
                {
                  path: "/nexus/benchmark",
                  element: <NexusBenchmark />,
                },
                {
                  path: "/nexus/settings",
                  element: <NexusSettings />,
                },
              ]
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // INTEGRATION HUB
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Access Rights']} />,
          children: [
            {
              path: "/integrations",
              element: <IntegrationHub />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // PROJECTS MODULE
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Access Rights', 'Project / Viewer', 'Project / User', 'Project / Manager']} requiredApp="projects" />,
          children: [
            {
              path: "/projects",
              element: <AllProjectsOverview />,
            },
            {
              path: "/projects/overview",
              element: <AllProjectsOverview />,
            },
            {
              path: "/projects/dashboard",
              element: <ProjectsModuleDashboard />,
            },
            {
              path: "/projects/list",
              element: <ProjectsList />,
            },
            {
              path: "/projects/my-tasks",
              element: <MyTasksView />,
            },
            {
              path: "/projects/workload",
              element: <WorkloadView />,
            },
            {
              path: "/projects/:id/overview",
              element: <ProjectOverview />,
            },
            {
              path: "/projects/:id/board",
              element: <ProjectBoard />,
            },
            {
              path: "/projects/:id/list",
              element: <ProjectListView />,
            },
            {
              path: "/projects/:id/gantt",
              element: <ProjectGantt />,
            },
            {
              path: "/projects/:id/milestones",
              element: <ProjectMilestones />,
            },
            {
              path: "/projects/:id/timesheets",
              element: <ProjectTimesheets />,
            },
            {
              path: "/projects/:id/sprints",
              element: <ProjectSprints />,
            },
            {
              path: "/projects/:id/reports",
              element: <ProjectReports />,
            },
            {
              path: "/projects/timesheets",
              element: <TimesheetApprovalDashboard />,
            },
            {
              element: <ProtectedRoute allowedGroups={['Project / Manager', 'Administration / Settings']} />,
              children: [
                {
                  path: "/projects/templates",
                  element: <ProjectTemplates />,
                },
                {
                  path: "/projects/:id/settings",
                  element: <ProjectOverview />,
                },
              ]
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // CALENDAR MODULE (installed via App Store)
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute requiredApp="calendar" />,
          children: [
            {
              path: "/calendar/*",
              element: <CalendarApp />,
            },
            {
              path: "/calendar/settings/integrations",
              element: <CalendarIntegrations />,
            },
            {
              path: "/calendar/integrations/callback",
              element: <IntegrationCallback />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // APPOINTMENTS MODULE (installed via App Store)
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute requiredApp="appointments" />,
          children: [
            {
              path: "/appointments",
              element: <AppointmentsDashboard />,
            },
            {
              path: "/appointments/working-hours",
              element: <WorkingHours />,
            },
            {
              path: "/appointments/:id",
              element: <AppointmentDetail />,
            },
            {
              path: "/book/service/:id",
              element: <ServiceBooking />,
            },
          ]
        },

        // ──────────────────────────────────────────────────────────
        // HUDDLE — COLLABORATION
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Huddle / Viewer', 'Huddle / User', 'Huddle / Manager']} requiredApp="huddle" />,
          children: [
            {
              path: "/huddle",
              element: <HuddlePage />,
            },
          ],
        },

        // ──────────────────────────────────────────────────────────
        // ADMIN MONITORING
        // ──────────────────────────────────────────────────────────
        {
          element: <ProtectedRoute allowedGroups={['Administration / Settings', 'Administration / Monitoring Viewer', 'Administration / Monitoring Manager']} requiredApp="admin_monitoring" />,
          children: [
            {
              path: "/admin/monitoring",
              element: <MonitoringLayout />,
              children: [
                { index: true, element: <MonitoringDashboard /> },
                { path: "presence", element: <LivePresence /> },
                { path: "activity", element: <ActivityLogs /> },
                { path: "sessions", element: <ActiveSessions /> },
                { path: "last-seen", element: <LastSeenReport /> },
              ],
            },
          ],
        },

        // ──────────────────────────────────────────────────────────
        // GLOBAL SEARCH & LOGOUT
        // ──────────────────────────────────────────────────────────
        {
          path: "/search",
          element: <GlobalSearchResults />,
        },
        {
          path: "/logout",
          element: <Logout />,
        },
      ],
    },
  ];

  const routesForNotAuthenticatedOnly = [
    {
      path: "/",
      element: <Homepage />
    },
    {
      path: "/login",
      element: <Login />,
    },
    {
      path: "/mfa/verify",
      element: <MFAVerify />,
    },
    {
      path: "/register",
      element: <Register />,
    },
  ];

  // Give every top-level route an errorElement so a render/chunk error
  // anywhere in its subtree shows RouteErrorBoundary instead of RR's
  // default "Unexpected Application Error!" page. Children without their
  // own errorElement bubble up to this one automatically.
  const withErrorBoundary = (routes) =>
    routes.map((route) => ({ errorElement: <RouteErrorBoundary />, ...route }));

  const router = createBrowserRouter(
    withErrorBoundary([
      ...publicRoutes,
      ...(!token ? routesForNotAuthenticatedOnly : []),
      ...(token ? routesForAuthenticatedOnly : []),
      {
        path: "*",
        element: <Navigate to="/" replace />,
      }
    ])
  );

  return (
    <Suspense fallback={<div className="flex items-center justify-center h-screen">Loading...</div>}>
      <RouterProvider router={router} />
    </Suspense>
  );
};

export default Routes;